import React, { useState, useEffect, useRef } from 'react';
import { SimulationSettings, CustomerProfile, FeedbackData, Message } from '../types';
import { useLiveSession } from '../hooks/useLiveSession';
import { useTimer } from '../hooks/useTimer';
import { parseGeminiJsonResponse, getUserFriendlyErrorMessage } from '../utils';
import { getLanguageConfig, isNonEnglish } from '../utils/languages';
import { Send, RotateCcw, Phone, PhoneOff, MicOff, Mic, Mail, Lightbulb } from 'lucide-react';

interface ChatInterfaceProps {
    settings: SimulationSettings;
    profile: CustomerProfile;
    chatSessionId: string | null;
    systemPrompt: string;
    agentNotes?: string;
    onFeedbackGenerated: (feedback: FeedbackData | null, timingDetails: string) => void;
    onReset: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
    settings,
    profile,
    chatSessionId,
    systemPrompt,
    agentNotes: initialAgentNotes,
    onFeedbackGenerated,
    onReset
}) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [aiIsThinking, setAiIsThinking] = useState(false);
    const [agentNotes, setAgentNotes] = useState(initialAgentNotes || settings.agentNotes || '');
    const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);
    const [streamingContent, setStreamingContent] = useState<{ speaker: 'user' | 'customer', text: string } | null>(null);
    const [activeCustomerName, setActiveCustomerName] = useState(profile.name);

    const [showGuide, setShowGuide] = useState(false);
    const [isGuideDisabled, setIsGuideDisabled] = useState<boolean>(Boolean(settings.conversationGuideDisabled));
    const [guideSuggestion, setGuideSuggestion] = useState('');
    const [isGuideLoading, setIsGuideLoading] = useState(false);
    const [guideTrigger, setGuideTrigger] = useState(0);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatDisplayRef = useRef<HTMLDivElement>(null);
    const initializedRef = useRef(false);
    const inputValueRef = useRef('');

    const toneAnalysisRef = useRef<string | null>(null);
    const isRequestingToneSummaryRef = useRef(false);

    // Dummy support department that receives the customer's emails (instead of the agent's app username)
    const supportDepartment = settings.supportDepartment || 'Customer Support Team';
    // Language/culture configuration for the selected simulation language
    const langConfig = getLanguageConfig(settings.language);
    const outputLanguageRule = isNonEnglish(settings.language)
        ? `\n**OUTPUT LANGUAGE (MANDATORY):** Write ALL human-readable text in ${langConfig.promptName}. This includes every summary, positive point, improvement point, recommended script, explanation, and timing analysis. Keep JSON keys, category keys, and numbers exactly as specified in English/snake_case — only the text VALUES must be in ${langConfig.promptName}.\nThe agent's documentation/notes are expected to be written in ${langConfig.promptName}; evaluate their quality in that language.\n`
        : '';
    // Tracks whether the thread has started, so only the very first email has a fresh (non-"Re:") subject
    const emailThreadStartedRef = useRef(false);

    const formatAsEmail = (content: string, from: string, to: string, isCustomer: boolean) => {
        console.log('=== formatAsEmail DEBUG ===');
        console.log('Input content:', content);
        console.log('From:', from, 'To:', to, 'isCustomer:', isCustomer);

        // Filter out actual email transmission headers and persona tags accidentally output by AI
        const headerRegex = /^(From|To|Subject|Date|Sent|Reply-To|Return-Path|Cc|Bcc|Gender|Great|Good|Status):[ \t]*/i;
        const lines = content.split('\n');
        const cleanLines = lines.filter((line, index) => {
            const trimmed = line.trim();
            if (!trimmed) return true;
            return !headerRegex.test(trimmed);
        });

        const bodyContent = cleanLines.join('\n').trim();
        const bodyLines = bodyContent.split('\n');
        const closings = ['best regards', 'kind regards', 'regards', 'sincerely', 'thank you', 'thanks', 'cheers', 'best', 'all the best', 'respectfully', 'cordially', langConfig.emailClosing.toLowerCase().replace(/[.,!。、،]$/, '')];
        const nameToMatch = from.toLowerCase().trim();

        let signatureFoundAt = -1;
        let meaningfulCount = 0;

        // Search from the bottom up (max 4 meaningful lines)
        for (let i = bodyLines.length - 1; i >= 0 && meaningfulCount < 4; i--) {
            const line = bodyLines[i].trim();
            if (!line) continue;
            meaningfulCount++;

            const lowerLine = line.toLowerCase().replace(/[.,!。、،]$/, ''); // Remove punctuation for matching (incl. CJK/Arabic)
            const isNameMatch = lowerLine === nameToMatch;
            const isAnyProperName = /^[A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,2}$/.test(line);
            const isClosing = closings.some(c => lowerLine === c);

            // If we found a proper name at the bottom, or exactly the name we expect, it's a signature line
            if (isNameMatch || (isCustomer && isAnyProperName)) {
                signatureFoundAt = i;
            } else if (isClosing) {
                // If we hit a closing line, everything from here down is the signature
                signatureFoundAt = i;
                break; // Found the very top of the signature block
            } else if (meaningfulCount > 1) {
                // Once we hit a non-signature line (and we've already looked at the very bottom), stop
                break;
            }
        }

        const finalBody = signatureFoundAt !== -1
            ? bodyLines.slice(0, signatureFoundAt).join('\n').trim()
            : bodyContent;

        console.log('Final body (signature stripped):', finalBody);

        // Construct professional unified header
        const baseSubject = `${settings.scenarioType}${profile.accountId ? ` - Account ${profile.accountId}` : ''}`;
        const subject = emailThreadStartedRef.current ? `Re: ${baseSubject}` : baseSubject;
        emailThreadStartedRef.current = true;

        const signature = isCustomer
            ? `${langConfig.emailClosing}\n${from}`
            : `${langConfig.emailClosing}\n${settings.userName}\n${supportDepartment}`;

        const fromHeader = isCustomer ? from : `${from} (${supportDepartment})`;
        return `From: ${fromHeader}\nTo: ${to}\nSubject: ${subject}\n\n${finalBody}\n\n${signature}`;
    };
    const { displayTime, startTimer, stopTimer } = useTimer();

    const addSystemMessage = (text: string) => {
        setMessages(prev => [...prev, { speaker: 'system', message: text, timestamp: new Date() }]);
    };

    const addUserMessage = (text: string) => {
        setMessages(prev => [...prev, { speaker: 'user', message: text, timestamp: new Date() }]);
        setGuideTrigger(prev => prev + 1);
    };

    const addCustomerMessage = (text: string) => {
        setMessages(prev => [...prev, { speaker: 'customer', message: text, timestamp: new Date() }]);
    };

    const liveSession = useLiveSession({
        onMessage: (speaker, text, isStreaming) => {
            if (isRequestingToneSummaryRef.current && speaker === 'customer') {
                if (text.includes('{') || text.includes('tone_analysis') || text.includes('phonetic_clarity')) {
                    if (!isStreaming) {
                        toneAnalysisRef.current = text;
                    }
                    return; // Intercept and hide from UI
                }
            }

            if (isStreaming) {
                setStreamingContent({ speaker: speaker as 'user' | 'customer', text });
            } else {
                setStreamingContent(null);
                if (speaker === 'user') addUserMessage(text);
                else if (speaker === 'customer') addCustomerMessage(text);
            }
        },
        onTurnComplete: () => {
            setStreamingContent(null);
        },
        onError: (err) => {
            addSystemMessage(err);
        },
        onDisconnect: () => {
            stopTimer();
        }
    });

    useEffect(() => {
        // Prevent duplicate initialization on re-renders
        if (initializedRef.current) return;
        initializedRef.current = true;

        // Use actual customer name from profile (includes KB data if uploaded)
        addSystemMessage(`Session active. Scenario: ${settings.scenarioType}.`);

        if (settings.channel === 'Email') {
            addSystemMessage(`Initiating email sequence. ${profile.name} will email ${supportDepartment} shortly.`);
            triggerAiOpeningMessage();
        } else if (settings.channel === 'Voice') {
            addSystemMessage("Voice bridge ready. Click 'Start Training Call' to begin.");
        } else {
            addSystemMessage(`Connected to Customer. Send your first message to start the timer.`);
        }
    }, []);

    // Timer starts when Voice session becomes active
    useEffect(() => {
        if (liveSession.isActive) {
            startTimer();
        } else if (settings.channel === 'Voice') {
            stopTimer();
        }
    }, [liveSession.isActive]);

    useEffect(() => {
        if (chatDisplayRef.current) {
            chatDisplayRef.current.scrollTop = chatDisplayRef.current.scrollHeight;
        }
    }, [messages, streamingContent, aiIsThinking]);

    const triggerAiOpeningMessage = async () => {
        setLoading(true);
        setAiIsThinking(true);

        try {
            const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
            const { db, auth, geminiProxy } = await import('../firebase');

            // Use geminiProxy (type: GENERATE for plain text output without JSON artifacts)
            // We ask the AI to generate the email body based on the system prompt instruction
            const instruction = "Compose the INITIAL EMAIL you are sending to the company's customer support inbox about your concern. This is asynchronous written correspondence: nobody has contacted you, greeted you, or connected with you yet, and nobody is reading in real time. Write it the way a real customer writes a fresh email: a natural written greeting (e.g., 'Hello,' or 'Hi,' or 'Good day,'), the reason you are writing, the relevant details and background, and what you want the company to do. NEVER use live chat or phone language (e.g., 'I thought I was connected', 'Are you there?', 'Hello? Anyone there?', 'Can you hear me?', 'I've been on hold', 'thanks for picking up'). Write ONLY the email message body. CRITICAL: DO NOT include email headers (From/To/Subject). DO NOT include ANY signature, sign-off, or closing (like 'Best regards' or 'Sincerely'). REALISM: NEVER use placeholders in brackets like '[account number]'. Instead, invent realistic fictional details." + ` LANGUAGE: Write the email entirely in ${langConfig.promptName}, using the natural email conventions and courtesy norms of that culture.`;

            const result: any = await geminiProxy({
                type: 'GENERATE',
                systemInstruction: systemPrompt,
                prompt: instruction
            });
            const aiResponseText = result.data.text;

            // Try to detect the customer's name from the response
            let currentDetectedName = activeCustomerName;

            // If the name is already set from the Knowledge Base (i.e., not "Customer"), 
            // we should be EXTREMELY skeptical of changing it based on chat snippets.
            const isNameFromKB = profile.name && profile.name !== "Customer";

            // Regexes for name detection: triggers are case-insensitive, but names MUST be capitalized.
            // We split these to ensure the capture group itself is case-sensitive.
            const patterns = [
                { trigger: /my name is/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /this is/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /regards,/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /thanks,!/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /sincerely,/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /best,/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /(?:i am|i'm|i am named)/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ }
            ];

            let foundMatch = null;
            for (const p of patterns) {
                // Look for trigger followed by space followed by capitalized words
                const fullRegex = new RegExp(p.trigger.source + "[\\s'\"\\(]*" + p.namePart.source, "m");
                const m = aiResponseText.match(fullRegex);
                if (m && m[1]) {
                    foundMatch = m[1].trim();
                    break;
                }
            }

            if (foundMatch) {
                const possibleName = foundMatch;
                const nameParts = possibleName.split(/\s+/);

                if (nameParts.length >= 1 && nameParts.length <= 4) {
                    const blacklist = [
                        'customer', 'agent', 'support', 'help', 'representative', 'the', 'this', 'that', 'i', 'my', 'is', 'am', 'are', 'was', 'were',
                        'writing', 'checking', 'calling', 'contacting', 'looking', 'trying', 'hoping', 'wanting', 'pleased', 'happy', 'sorry',
                        'a', 'an', 'and', 'with', 'for', 'from', 'to', 'in', 'on', 'at', 'by', 'of', 'great', 'good', 'bad', 'wonderful', 'excellent',
                        'urgent', 'immediate', 'thank', 'thanks', 'order', 'account', 'issue', 'problem', 'request', 'status'
                    ];

                    if (!blacklist.includes(nameParts[0].toLowerCase())) {
                        // If we have a KB name, only update if the detected name is a solid multi-word proper noun 
                        // or if the current name is generic.
                        const shouldUpdate = !isNameFromKB || (nameParts.length >= 2 && !activeCustomerName.includes(nameParts[0]));

                        if (shouldUpdate) {
                            console.log(`Detected valid AI customer name: ${possibleName}. Updating state.`);
                            currentDetectedName = possibleName;
                            setActiveCustomerName(possibleName);
                        }
                    }
                }
            }

            // Unified realistic typing delay
            const wordCount = aiResponseText.split(/\s+/).length;
            const typingDelay = Math.min(wordCount * 250 + 2000, 12000);
            await new Promise(resolve => setTimeout(resolve, typingDelay));

            // Format as email
            const formattedEmail = formatAsEmail(aiResponseText, currentDetectedName, supportDepartment, true);

            if (chatSessionId && auth.currentUser) {
                const messagesRef = collection(db, "chatSessions", chatSessionId, "messages");
                // Prepend initiation message
                await addDoc(messagesRef, {
                    role: "user",
                    text: "[Simulation setup: You are the customer. Compose your initial email to the company's customer support inbox. Email is asynchronous written correspondence, not a live chat or call. The support agent's written replies will follow.]",
                    timestamp: serverTimestamp(),
                    sequence: -1
                });
                // Then the model opening
                await addDoc(messagesRef, {
                    role: "model",
                    text: formattedEmail,
                    sequence: 0,
                    timestamp: serverTimestamp()
                });
            }

            addCustomerMessage(formattedEmail);

            // Start timer when customer's first email arrives
            startTimer();
        } catch (error) {
            addSystemMessage(getUserFriendlyErrorMessage(error));
        } finally {
            setLoading(false);
            setAiIsThinking(false);
        }
    };

    const handleSendMessage = async () => {
        console.log('=== handleSendMessage called ===');
        console.log('inputValue (state):', inputValue);
        console.log('inputValueRef.current (ref):', inputValueRef.current);
        console.log('inputValue length:', inputValue.length);
        console.log('inputValueRef length:', inputValueRef.current.length);

        // Use ref as source of truth since state might be stale
        const actualValue = inputValueRef.current || inputValue;
        console.log('Using actualValue:', actualValue);

        if (!actualValue.trim() || loading) return;

        const message = actualValue.trim();
        console.log('message after trim:', message);

        setInputValue('');
        inputValueRef.current = '';

        // Format as email if Email channel
        const formattedMessage = settings.channel === 'Email'
            ? formatAsEmail(message, settings.userName, activeCustomerName, false)
            : message;

        // Optimistically update UI
        addUserMessage(formattedMessage);
        setLoading(true);

        // Start timer only for Chat channel (Email timer starts when customer's email arrives)
        if (settings.channel === 'Chat') {
            startTimer();
        }

        // Show typing indicator for both channels
        setAiIsThinking(true);

        try {
            const { geminiProxy } = await import('../firebase');

            if (!chatSessionId) throw new Error("No chat session ID");

            // Use geminiProxy (type: CHAT)
            // The backend handles history loading and saving both User and Model messages to Firestore.
            const result: any = await geminiProxy({
                type: 'CHAT',
                chatSessionId: chatSessionId,
                message: formattedMessage // Send the formatted message so AI sees headers (and backend saves it)
            });

            const aiResponseText = result.data.text;

            // Try to detect the customer's name from the response
            let currentDetectedName = activeCustomerName;

            const isNameFromKB = profile.name && profile.name !== "Customer";

            const patterns = [
                { trigger: /my name is/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /this is/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /regards,/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /thanks,!/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /sincerely,/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /best,/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ },
                { trigger: /(?:i am|i'm|i am named)/i, namePart: /([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2})/ }
            ];

            let foundMatch = null;
            for (const p of patterns) {
                const fullRegex = new RegExp(p.trigger.source + "[\\s'\"\\(]*" + p.namePart.source, "m");
                const m = aiResponseText.match(fullRegex);
                if (m && m[1]) {
                    foundMatch = m[1].trim();
                    break;
                }
            }

            if (foundMatch) {
                const possibleName = foundMatch;
                const nameParts = possibleName.split(/\s+/);

                if (nameParts.length >= 1 && nameParts.length <= 4) {
                    const blacklist = [
                        'customer', 'agent', 'support', 'help', 'representative', 'the', 'this', 'that', 'i', 'my', 'is', 'am', 'are', 'was', 'were',
                        'writing', 'checking', 'calling', 'contacting', 'looking', 'trying', 'hoping', 'wanting', 'pleased', 'happy', 'sorry',
                        'a', 'an', 'and', 'with', 'for', 'from', 'to', 'in', 'on', 'at', 'by', 'of', 'great', 'good', 'bad', 'wonderful', 'excellent',
                        'urgent', 'immediate', 'thank', 'thanks', 'order', 'account', 'issue', 'problem', 'request', 'status'
                    ];
                    if (!blacklist.includes(nameParts[0].toLowerCase())) {
                        const shouldUpdate = !isNameFromKB || (nameParts.length >= 2 && !activeCustomerName.includes(nameParts[0]));
                        if (shouldUpdate) {
                            console.log(`Detected valid AI customer name: ${possibleName}. Updating state.`);
                            currentDetectedName = possibleName;
                            setActiveCustomerName(possibleName);
                        }
                    }
                }
            }

            // Unified realistic typing delay
            const wordCount = aiResponseText.split(/\s+/).length;
            const typingDelay = Math.min(wordCount * 250 + 2000, 12000);

            if (typingDelay > 0) {
                await new Promise(resolve => setTimeout(resolve, typingDelay));
            }

            // Format AI response as email if Email channel
            const formattedResponse = settings.channel === 'Email'
                ? formatAsEmail(aiResponseText, currentDetectedName, supportDepartment, true)
                : aiResponseText;

            addCustomerMessage(formattedResponse);
        } catch (error) {
            addSystemMessage(getUserFriendlyErrorMessage(error));
        } finally {
            setLoading(false);
            setAiIsThinking(false);
        }
    };

    const handleEndSimulation = async () => {
        if (isGeneratingFeedback) return;
        setIsGeneratingFeedback(true);
        
        if (liveSession.isActive) {
            if (settings.channel === 'Voice') {
                addSystemMessage("Ending session... Generating tone and phonetic analysis from audio...");
                isRequestingToneSummaryRef.current = true;
                liveSession.sendTextMessage('[REQUEST_TONE_SUMMARY]');
                
                // Wait up to 4 seconds for the AI to return the tone summary
                for (let i = 0; i < 40; i++) {
                    if (toneAnalysisRef.current) break;
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }
            await liveSession.stopSession();
        }
        stopTimer();

        const fullFeedbackPrompt = `You are an expert customer service quality analyst. Analyze the following interaction transcript and provide detailed, actionable feedback.

- Customer Name (Simulation): ${profile.name || "Customer"}
- Agent Name (Real User): ${settings.userName} (Note: Agents are allowed to use aliases or preferred names during the interaction. Do not penalize them if they introduce themselves with a different name).
- Industry: ${settings.industry}
- Scenario: ${settings.scenarioType}
- Channel: ${settings.channel}
- Customer Persona: ${settings.customerPersona}
- Language: ${settings.language}
- Total Duration: ${displayTime}

${toneAnalysisRef.current ? `**LIVE AUDIO TONE SUMMARY (CRITICAL SOURCE OF TRUTH FOR VOCAL DELIVERY):**\n${toneAnalysisRef.current}\nUse this exact raw audio analysis to grade empathy_and_tone and communication_clarity categories.` : ''}

**CRITICAL: SOURCE OF TRUTH**
The customer's identity, name, gender, and facts are strictly defined by the Knowledge Base provided during setup.
- Reference Name: ${profile.name}
- Reference Gender: ${profile.gender}
- Reference Account: ${profile.accountId || 'Not specified'}

**CRITICAL: IDENTITY & HALLUCINATION PREVENTION**
The customer being simulated is **${profile.name}**. 
IMPORTANT: You MUST NOT invent, hallucinate, or use any other name for the customer in your feedback. The name you return in "customer_name_detected" MUST match the name the customer actually used in the transcript, which should align with "${profile.name}".

**TRANSCRIPT:**
${messages.map(m => `${m.speaker.toUpperCase()}: ${m.message}`).join('\n')}

**AGENT NOTES:**
${agentNotes || 'No notes provided'}
${settings.knowledgeBaseText ? `
**KNOWLEDGE BASE (SINGLE SOURCE OF TRUTH FOR FACTS):**
The customer's facts, the correct resolution information, and any policies come ONLY from this Knowledge Base. Use it to judge whether the agent's statements were accurate. Do NOT reward or penalize the agent based on assumptions or general knowledge that contradicts it.
=== KNOWLEDGE BASE START ===
${settings.knowledgeBaseText}
=== KNOWLEDGE BASE END ===
` : ''}

**LANGUAGE & CULTURAL NUANCES:**
The user has chosen to interact in: **${langConfig.promptName}**.
CRITICAL: You MUST factor in the specific nuances of ${langConfig.promptName} when assessing for tone, emotion, communication, clarity, and soft skills. Do not apply a rigid English-centric lens to other languages. Adjust your scoring for empathy, tone, and clarity to reflect what is considered polite, empathetic, and clear IN THAT SPECIFIC LANGUAGE AND CULTURE.

- ${langConfig.assessmentGuidance}
${outputLanguageRule}
${settings.assessmentRubricText ? `**SCOPE OF LANGUAGE GUIDANCE:** The language and cultural considerations above are ONLY a lens for interpreting the agent's behavior WITHIN the selected rubric's own categories and scoring descriptors. They NEVER add categories, change weights, change the scoring formula, or override the rubric or the Additional AI Instructions below.
` : ''}

${settings.assessmentRubricText ? `
**⚠️ STRICT RUBRIC ENFORCEMENT — ASSESSMENT FRAMEWORK: "${settings.assessmentRubricTitle || 'Custom Form'}"**

You MUST evaluate this interaction using ONLY the rubric defined below.
- Do NOT apply any other scoring categories, weights, or criteria outside of this rubric.
- Do NOT add, remove, or rename categories.
- Do NOT blend this rubric with any generic customer service frameworks.
- The rubric below is the ABSOLUTE and ONLY valid assessment framework for this session.
- Follow the scoring descriptors, weights, rules, and formula EXACTLY as written in the rubric.
- If the rubric defines a category as mandatory, set "not_applicable" to false for that category regardless of the interaction.

=== ASSESSMENT RUBRIC (STRICT — FOLLOW EXACTLY) ===
${settings.assessmentRubricText}
=======================================================
${settings.assessmentRubricInstructions ? `\n**ADDITIONAL AI INSTRUCTIONS (HUMAN OBSERVATIONS & REFINEMENTS):**\nThe following instructions were written by the administrator to make your assessments and feedback align closely with human evaluators. These are form-specific core instructions. YOU MUST STRICTLY FOLLOW THEM, including any negative prompts (e.g., "do not do X", "deduct points if Y"). They refine how you interpret and score the rubric's criteria and how you write feedback, and they take precedence over ALL generic, language, and cultural guidance in this prompt. Do not use them as a reason to add, remove, or rename rubric categories unless they explicitly instruct you to.\n\n${settings.assessmentRubricInstructions}\n=======================================================\n` : `\n**ADDITIONAL AI INSTRUCTIONS:**\nNone provided for this specific form. Simply follow the exact rubric defined above without any external or generic evaluation rules (language/cultural nuance may only be used to interpret behavior within the rubric's own criteria).\n=======================================================\n`}

**ASSESSMENT PRECEDENCE (HIGHEST FIRST):**
1. The selected rubric "${settings.assessmentRubricTitle || 'Custom Form'}" defines the STRUCTURE: categories, descriptors, weights, formula, and mandatory rules.
2. The Additional AI Instructions from the administrator define HOW to interpret and score within that structure, and how to write feedback. Where they are more specific than the rubric's wording, follow them.
3. Knowledge Base accuracy (facts the agent should have used).
4. Language and cultural nuance (interpretation only).

**KB ACCURACY OVERLAY (apply within rubric categories where relevant):**
1. Did the agent correctly identify and respect the facts in the Knowledge Base?
2. Did the agent use the customer's correct name (${profile.name}) and details? (Remember: The agent is allowed to use an alias for their own name).
3. Did the agent provide info that contradicts the KB?
Fold these KB adherence observations into whichever rubric categories are most relevant (e.g., accuracy, compliance, product knowledge) — do not create new categories for them.

**RETURN FORMAT:**
You MUST return ONLY a valid JSON object (no markdown, no explanation text). Use this structure, where each key in "feedback" corresponds EXACTLY to the snake_case category keys defined in the rubric above:

{
  "overall_score": <number 0-100, calculated using the formula defined in the rubric>,
  "overall_summary": "<2-3 sentence summary of performance against this rubric>",
  "customer_name_detected": "<the name the AI customer used in the transcript>",
  "feedback": {
    "<rubric_category_key_1>": {
      "score": <number, within the max_score defined in rubric>,
      "max_score": <number as defined in rubric>,
      "positive": ["<specific positive point 1>", "<specific positive point 2>"],
      "improvement": ["<specific improvement 1>", "<specific improvement 2>"],
      "recommended_scripts": ["<example script 1>"],
      "explanation_for_deduction": "<why points were deducted, referencing rubric criteria>",
      "not_applicable": <true/false, as defined by rubric rules>,
      "estimated_time_spent_seconds": <number or null>
    }
  },
  "timing_analysis_summary": "<analysis of pacing, response times, and time management>"${isNonEnglish(settings.language) ? `,
  "category_labels": { "<rubric_category_key_1>": "<human-readable category name translated into ${langConfig.promptName}>" }` : ''}
}

**CRITICAL REQUIREMENTS:**
- Return ONLY the JSON object, no other text
- Use ONLY the category keys from the rubric above — no extras, no omissions
- Each category MUST have all required fields
- Scores must be realistic, justified, and reference the rubric's own scoring descriptors
- Provide at least 2-3 specific points for positive and improvement arrays
- Be constructive and actionable in feedback
` : `
**EVALUATION CRITERIA:**
1. **KB Adherence**: Did the agent correctly identify and respect the facts in the Knowledge Base?
2. **Identity Recognition**: Did the agent use the customer's correct name (${profile.name}) and details? (Remember: The agent is allowed to use an alias for their own name).
3. **Accuracy**: Did the agent provide info that contradicts the KB?

**INSTRUCTIONS:**
Evaluate this interaction across 8 key categories. For EACH category, provide:
1. A score (0-10) based on performance
2. Specific positive observations (what was done well)
3. Specific areas for improvement (what could be better)
4. Recommended scripts or phrases (if applicable)
5. Explanation for any point deductions
6. Whether the category is applicable to this interaction
7. Estimated time spent in seconds (if possible, based on the transcript flow)

**SCORING GUIDELINES:**
- 9-10: Exceptional - Exceeded expectations
- 7-8: Good - Met expectations with minor areas for improvement
- 5-6: Satisfactory - Met basic requirements but needs improvement
- 3-4: Needs Improvement - Below expectations
- 0-2: Poor - Significant issues

**CRITICAL: DOCUMENTATION AND NOTE-TAKING SCORING RULES:**
This category is MANDATORY and NEVER "not_applicable". Score strictly as follows:
- **0 points**: No notes provided (agent notes are empty or contain only whitespace)
- **1-3 points**: Minimal notes (less than 10 words, vague, or incomplete)
- **4-6 points**: Basic notes (captures some key points but missing important details)
- **7-8 points**: Good notes (comprehensive, captures most important details)
- **9-10 points**: Excellent notes (complete, detailed, well-organized, includes all key information: customer issue, actions taken, resolution, next steps)

The agent MUST take notes during every interaction. Empty or missing notes result in automatic 0 score for this category.

**RETURN FORMAT:**
You MUST return ONLY a valid JSON object (no markdown, no explanation text). Use this EXACT structure:

{
  "overall_score": <number 0-100, calculated from category scores>,
  "overall_summary": "<2-3 sentence summary of performance>",
  "customer_name_detected": "<the name the AI customer used in the transcript, or the one provided below if no name was mentioned>",
  "feedback": {
    "greeting_and_opening": {
      "score": <0-10>,
      "max_score": 10,
      "positive": ["<specific positive point 1>", "<specific positive point 2>"],
      "improvement": ["<specific improvement 1>", "<specific improvement 2>"],
      "recommended_scripts": ["<example script 1>", "<example script 2>"],
      "explanation_for_deduction": "<why points were deducted>",
      "not_applicable": <true/false>,
      "estimated_time_spent_seconds": <number or null>
    },
    "active_listening_and_understanding": {
      "score": <0-10>,
      "max_score": 10,
      "positive": ["<point>"],
      "improvement": ["<point>"],
      "recommended_scripts": ["<script>"],
      "explanation_for_deduction": "<reason>",
      "not_applicable": <true/false>,
      "estimated_time_spent_seconds": <number or null>
    },
    "empathy_and_tone": {
      "score": <0-10>,
      "max_score": 10,
      "positive": ["<point>"],
      "improvement": ["<point>"],
      "recommended_scripts": ["<script>"],
      "explanation_for_deduction": "<reason>",
      "not_applicable": <true/false>,
      "estimated_time_spent_seconds": <number or null>
    },
    "problem_solving_resolution": {
      "score": <0-10>,
      "max_score": 10,
      "positive": ["<point>"],
      "improvement": ["<point>"],
      "recommended_scripts": ["<script>"],
      "explanation_for_deduction": "<reason>",
      "not_applicable": <true/false>,
      "estimated_time_spent_seconds": <number or null>
    },
    "communication_clarity": {
      "score": <0-10>,
      "max_score": 10,
      "positive": ["<point>"],
      "improvement": ["<point>"],
      "recommended_scripts": ["<script>"],
      "explanation_for_deduction": "<reason>",
      "not_applicable": <true/false>,
      "estimated_time_spent_seconds": <number or null>
    },
    "compliance_and_accuracy": {
      "score": <0-10>,
      "max_score": 10,
      "positive": ["<point>"],
      "improvement": ["<point>"],
      "recommended_scripts": ["<script>"],
      "explanation_for_deduction": "<reason>",
      "not_applicable": <true/false>,
      "estimated_time_spent_seconds": <number or null>
    },
    "documentation_and_note_taking": {
      "score": <0-10>,
      "max_score": 10,
      "positive": ["<point>"],
      "improvement": ["<point>"],
      "recommended_scripts": ["<script>"],
      "explanation_for_deduction": "<reason>",
      "not_applicable": <true/false>,
      "estimated_time_spent_seconds": <number or null>
    },
    "closing": {
      "score": <0-10>,
      "max_score": 10,
      "positive": ["<point>"],
      "improvement": ["<point>"],
      "recommended_scripts": ["<script>"],
      "explanation_for_deduction": "<reason>",
      "not_applicable": <true/false>,
      "estimated_time_spent_seconds": <number or null>
    }
  },
  "timing_analysis_summary": "<analysis of pacing, response times, and time management>"${isNonEnglish(settings.language) ? `,
  "category_labels": { "greeting_and_opening": "<category name in ${langConfig.promptName}>", "active_listening_and_understanding": "<...>", "empathy_and_tone": "<...>", "problem_solving_resolution": "<...>", "communication_clarity": "<...>", "compliance_and_accuracy": "<...>", "documentation_and_note_taking": "<...>", "closing": "<...>" }` : ''}
}

**CRITICAL REQUIREMENTS:**
- Return ONLY the JSON object, no other text
- Include "customer_name_detected": a string containing the name the AI customer used (e.g. "James", "Samantha", etc.)
- All 8 categories MUST be present
- Each category MUST have all required fields
- Scores must be realistic and justified
- Provide at least 2-3 specific points for positive and improvement arrays
- **DOCUMENTATION CATEGORY**: Set "not_applicable" to FALSE. Score 0 if agent notes are empty, 10 if complete and detailed
- Be constructive and actionable in feedback
`}
`;

        try {
            const { geminiProxy } = await import('../firebase');

            // Use geminiProxy (type: FEEDBACK)
            const result: any = await geminiProxy({
                type: 'FEEDBACK',
                prompt: fullFeedbackPrompt
            });

            const aiResponseText = result.data.text;

            const feedbackData = await parseGeminiJsonResponse(aiResponseText);
            if (feedbackData) {
                onFeedbackGenerated(feedbackData, `Total Time: ${displayTime}`);
            } else {
                throw new Error("Parsed feedback data was null");
            }
        } catch (error) {
            console.error("Error generating feedback:", error);
            onFeedbackGenerated(null, `Total Time: ${displayTime}`);
        } finally {
            setIsGeneratingFeedback(false);
        }
    };

    const fetchGuideSuggestion = async (isInitial = false) => {
        if (!showGuide) return;
        
        setIsGuideLoading(true);
        try {
            const { geminiProxy } = await import('../firebase');
            const transcript = messages
                .filter(m => m.speaker === 'user' || m.speaker === 'customer')
                .slice(-2)
                .map(m => `${m.speaker === 'user' ? 'Agent' : 'Customer'}: ${m.message}`)
                .join('\n');

            let kbContext = '';
            if (settings.knowledgeBaseText) {
                kbContext = `\n\nKNOWLEDGE BASE REFERENCE:\n${settings.knowledgeBaseText}\n\nUse the Knowledge Base above to determine what the agent should say next.`;
            }

            const isFirstTurn = messages.filter(m => m.speaker === 'user' || m.speaker === 'customer').length === 0;

            const prompt = isFirstTurn
                ? `You are an expert customer service script generator assisting an agent at the start of a session.
${kbContext}

Provide the exact opening spiel or the very first thing the agent should say to start this interaction effectively. Write ONLY the exact words the agent should speak. Do not include quotes, coaching, or explanations.
CRITICAL: You MUST write the script in ${langConfig.promptName}, in a natural, professional register that follows that culture's customer service etiquette.`
                : `You are an expert customer service script generator assisting an agent in real-time.
Review the following RECENT conversation transcript (last few messages).
${kbContext}

TRANSCRIPT:
${transcript}

Based on the Agent's latest message and the Knowledge Base, anticipate the Customer's response and generate the EXACT NEXT STATEMENT the Agent should say. 
Write ONLY the exact words the Agent should speak next. Do not include quotes, coaching, predictions, or explanations. Just provide the script.
CRITICAL: You MUST write the script in ${langConfig.promptName}, in a natural, professional register that follows that culture's customer service etiquette.`;

            const result: any = await geminiProxy({
                type: 'GENERATE',
                modelOverride: 'gemini-2.5-flash',
                prompt: prompt
            });

            setGuideSuggestion(result.data.text.trim());
        } catch (error) {
            console.error("Error fetching guide suggestion:", error);
            setGuideSuggestion("Unable to load suggestion. Please rely on your training.");
        } finally {
            setIsGuideLoading(false);
        }
    };

    useEffect(() => {
        let unsubscribe: (() => void) | undefined;
        (async () => {
            try {
                const { doc, onSnapshot } = await import('firebase/firestore');
                const { db, auth } = await import('../firebase');
                if (!auth.currentUser) return;
                unsubscribe = onSnapshot(doc(db, 'users', auth.currentUser.uid), (snap) => {
                    if (snap.exists()) {
                        const data = snap.data();
                        if (typeof data.conversationGuideDisabled === 'boolean') {
                            setIsGuideDisabled(data.conversationGuideDisabled);
                            settings.conversationGuideDisabled = data.conversationGuideDisabled;
                            if (data.conversationGuideDisabled) {
                                setShowGuide(false);
                            }
                        }
                    }
                });
            } catch (e) {
                console.warn('Could not attach live guide permission listener:', e);
            }
        })();
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (!showGuide || isGuideDisabled) return;
        
        if (guideTrigger > 0) {
            fetchGuideSuggestion(false);
        } else if (!guideSuggestion && !isGuideLoading) {
            fetchGuideSuggestion(true);
        }
    }, [guideTrigger, showGuide]);


    return (
        <section className="chat-layout">
            <div className="chat-box">
                <div className="chat-messages" ref={chatDisplayRef}>
                    {messages.map((msg, idx) => (
                        msg.speaker === 'system' ? (
                            <div key={idx} style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)', margin: '1rem 0', fontStyle: 'italic' }}>
                                {msg.message}
                            </div>
                        ) : (
                            <div key={idx} className={`message-bubble ${msg.speaker === 'user' ? 'message-agent' : 'message-customer'}`}>
                                {settings.channel === 'Email' && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                        <Mail size={16} style={{ color: 'var(--primary)' }} />
                                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>EMAIL</span>
                                    </div>
                                )}
                                <pre dir="auto" lang={langConfig.bcp47} style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, fontSize: settings.channel === 'Email' ? '0.9rem' : 'inherit' }}>{msg.message}</pre>
                            </div>
                        )
                    ))}
                    {streamingContent && (
                        <div className={`message-bubble ${streamingContent.speaker === 'user' ? 'message-agent' : 'message-customer'}`}>
                            <span className="message-label">{streamingContent.speaker === 'user' ? settings.userName : profile.name}</span>
                            {streamingContent.text}
                        </div>
                    )}
                    {aiIsThinking && (
                        <div
                            className="message-bubble message-customer"
                            style={{
                                background: '#F3F4F6',
                                border: '1px dashed var(--primary)',
                                marginBottom: '1.5rem',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div className="typing-indicator">
                                    <span></span><span></span><span></span>
                                </div>
                                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                                    {settings.channel === 'Email' ? `${activeCustomerName || 'Customer'} is composing an email` : 'Customer is typing'}...
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {settings.channel === 'Voice' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <div className="chat-input-area" style={{ justifyContent: 'center', padding: '1.5rem', gap: '1rem', borderTop: 'none' }}>
                            {!liveSession.isActive ? (
                                <button className="button button-primary" onClick={() => {
                                    const audioInstructions = `\n\n**POST-CALL SYSTEM COMMAND HOOK (DO NOT ACT AS EVALUATOR DURING THE CALL)**\nDuring the call itself, you are 100% the customer (${profile.name}) and NEVER an evaluator, coach, or support agent.\nOnly at the very end of the session, when the system sends the exact text token [REQUEST_TONE_SUMMARY], reply with ONLY a JSON block summarizing the agent's vocal tone and phonetic clarity (respecting the unique rules, customs, intonations, and phonetic peculiarities of ${langConfig.promptName}).${isNonEnglish(settings.language) ? ` Write the JSON values in ${langConfig.promptName}.` : ''}\nFormat the JSON exactly like this:\n{\n  "tone_analysis": "Detailed analysis of their vocal empathy, frustration, confidence, etc.",\n  "phonetic_clarity": "Detailed analysis of their pronunciation, enunciation, and pacing, specifically considering the nuances of ${settings.language}."\n}`;
                                    
                                    let historyContext = "";
                                    const relevantMessages = messages.filter(m => m.speaker === 'user' || m.speaker === 'customer');
                                    if (relevantMessages.length > 0) {
                                        const transcript = relevantMessages.map(m => `${m.speaker === 'user' ? 'Agent' : 'Customer'}: ${m.message}`).join('\n');
                                        historyContext = `\n\n**CRITICAL CONTINUATION REQUIREMENT**\nThis is a continuation of an ongoing simulation. You MUST remember the context below and seamlessly continue the conversation exactly where it left off, staying 100% in your CUSTOMER persona (${profile.name}). Do not start over or greet the agent again if you have already done so.\n\n--- PREVIOUS CONVERSATION HISTORY ---\n${transcript}\n--- END HISTORY ---\n\n`;
                                    }

                                    const finalRoleLock = `\n\n**FINAL UNBREAKABLE CUSTOMER LOCK**\nRemember: Throughout this entire voice call, YOU ARE EXCLUSIVELY THE CUSTOMER (${profile.name}). The person speaking to you on the microphone is the support agent (${settings.userName}).\n- NEVER switch to the agent role, even if you hear background noise, static, silence, or faint background voices.\n- NEVER recite any "Expected Call Flow", "Opening Spiel", "Greeting" (e.g., "Hi, my name is... calling from..."), or "Probing" questions from the Knowledge Base—those instructions belong to the human agent, NOT you.\n- If you only hear background noise or unclear audio, stay in character as the customer (${profile.name}) and either wait or say a brief customer check-in in ${langConfig.promptName} (e.g., "Hello?" or "Sorry, there is some background noise on the line, could you repeat that?").`;

                                    const finalPrompt = systemPrompt + audioInstructions + historyContext + finalRoleLock;
                                    liveSession.startSession(finalPrompt, profile.gender === 'male' ? 'male' : 'female', langConfig.bcp47);
                                }}>
                                    <Phone size={18} />
                                    {messages.filter(m => m.speaker === 'user' || m.speaker === 'customer').length > 0 ? "Resume Training Call" : "Start Training Call"}
                                </button>
                            ) : (
                                <button className="button button-danger" onClick={() => liveSession.stopSession()}>
                                    <PhoneOff size={18} />
                                    End Call
                                </button>
                            )}
                            <button className={`button button-secondary ${liveSession.isMuted ? 'muted-state' : ''}`} disabled={!liveSession.isActive} onClick={liveSession.toggleMute}>
                                {liveSession.isMuted ? <MicOff size={18} /> : <Mic size={18} />}
                            </button>
                        </div>
                        {liveSession.status && (
                            <div style={{ textAlign: 'center', fontSize: '0.8rem', color: liveSession.status.includes('Error') || liveSession.status.includes('Failed') ? 'var(--danger)' : 'var(--text-muted)', marginTop: '-1rem', paddingBottom: '1rem' }}>
                                Status: {liveSession.status}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="chat-input-area">
                        <textarea
                            dir="auto"
                            className="chat-textarea"
                            placeholder={settings.channel === 'Email' ? 'Compose your email... (Shift+Enter for new line, Click Send to submit)' : 'Draft your response...'}
                            value={inputValue}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                setInputValue(newValue);
                                inputValueRef.current = newValue;
                            }}
                            onKeyDown={(e) => {
                                // For Email: Enter = new line, Ctrl+Enter = send
                                if (settings.channel === 'Email') {
                                    if (e.key === 'Enter' && e.ctrlKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                    // In Email mode, let standard Enter create a newline
                                } else {
                                    // For Chat: Enter = send, Shift+Enter = new line
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }
                            }}
                            autoComplete="off"
                            rows={settings.channel === 'Email' ? 6 : 1}
                            disabled={loading}
                            style={settings.channel === 'Email' ? { minHeight: '150px', resize: 'vertical' } : {}}
                        />
                        <button className="button button-primary" style={{ padding: '0.65rem' }} onClick={handleSendMessage} disabled={loading}>
                            {settings.channel === 'Email' ? <Mail size={18} /> : <Send size={18} />}
                        </button>
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Session Clock</span>
                        <span style={{ fontSize: '1.1rem', fontWeight: 800, fontFamily: 'Outfit', color: 'var(--primary)' }}>{displayTime}</span>
                    </div>
                    {!isGuideDisabled && (
                        <button 
                            className={`button ${showGuide ? 'button-primary' : 'button-secondary'}`} 
                            style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}
                            onClick={() => setShowGuide(!showGuide)}
                        >
                            <Lightbulb size={16} />
                            {showGuide ? 'Hide Guide' : 'Enable Guide'}
                        </button>
                    )}
                    <button className="button button-secondary" style={{ width: '100%' }} onClick={onReset}>
                        <RotateCcw size={16} />
                        Abort Session
                    </button>
                    <button className="button button-danger" style={{ width: '100%', marginTop: 'auto' }} onClick={handleEndSimulation} disabled={isGeneratingFeedback}>
                        {isGeneratingFeedback ? 'Evaluating...' : 'Finish & Evaluate'}
                    </button>
                </div>

                {showGuide && !isGuideDisabled && (
                    <div className="card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', background: 'rgba(160, 43, 147, 0.05)', border: '1px solid rgba(160, 43, 147, 0.2)' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)' }}>Conversation Guide</span>
                        {isGuideLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)' }}>
                                <div className="typing-indicator" style={{ transform: 'scale(0.8)', margin: 0 }}>
                                    <span></span><span></span><span></span>
                                </div>
                                <span style={{ fontSize: '0.85rem' }}>Analyzing...</span>
                            </div>
                        ) : guideSuggestion ? (
                            <p style={{ fontSize: '0.9rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                                {guideSuggestion}
                            </p>
                        ) : (
                            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                Waiting for customer response...
                            </p>
                        )}
                    </div>
                )}

                <div className="card" style={{ flexGrow: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.75rem', display: 'block' }}>Scratchpad</span>
                    <textarea
                        dir="auto"
                        style={{ border: 'none', background: 'transparent', width: '100%', flexGrow: 1, resize: 'none', fontSize: '0.9rem', outline: 'none' }}
                        placeholder={isNonEnglish(settings.language) ? `Log customer details, commitments, or key takeaways here for scoring (write in ${langConfig.label})...` : "Log customer details, commitments, or key takeaways here for scoring..."}
                        value={agentNotes}
                        onChange={(e) => setAgentNotes(e.target.value)}
                        autoComplete="off"
                    />
                </div>
            </div>
        </section>
    );
};

